import { Category, Page } from '@packages/common';
import { waitForGtmEvent } from '../gtmEventsHelper';

import { GtmInteractor } from '../gtmInteractor';

const FALLBACK_VALUE = 'NV';
export interface CmsModuleEvent {
  [key: string]: any;
}

export const gtmStart = (gtm: GtmInteractor) => {
  return () => {
    gtm.pushOnce({ 'gtm.start': new Date().getTime() + 1000, event: 'gtm.js' });
  };
};

export const cmsModuleEvent = (gtm: GtmInteractor) => (data: CmsModuleEvent) => {
  return gtm.push({
    event: 'event',
    ...data,
  });
};

type PageLoadEventPayload = {
  category?: Category;
  pageType: Page;
  wishlistedProductsCount: number;
  //
  clientID: string;
  environment: string;
  locale: string;
  calendarWeek: number;
  abTestTitle: string;
  abTestFactor: number;
  originalLocation: string;
};

export const pageLoad =
  (gtm: GtmInteractor) =>
  ({
    category,
    pageType,
    wishlistedProductsCount,
    clientID,
    environment,
    locale,
    calendarWeek,
    abTestTitle,
    abTestFactor,
    originalLocation,
  }: PageLoadEventPayload) => {
    const activeNumberOfWishlistProducts = wishlistedProductsCount;

    return gtm.push({
      event: 'pageLoad',
      clientID,
      environment,
      pageCountry: locale,
      calendarWeek,
      pageType,
      categoryID: category?._id || FALLBACK_VALUE,
      categoryCode: category?.code || FALLBACK_VALUE,
      categoryPath: category?.url || FALLBACK_VALUE,
      activeNumberOfWishlistProducts,
      abTestTitle,
      abTestFactor,
      originalLocation,
    });
  };

export interface MarketingEventPayload {
  remarketingProductID?: string[];
  quantityOfProducts?: number;
  category?: Category;
  pageType: Page;
}

export const marketing = (gtm: GtmInteractor) => async (payload: MarketingEventPayload) => {
  const { remarketingProductID, quantityOfProducts, category, pageType } = payload;
  await waitForGtmEvent('sessionStart');
  return gtm.push({
    event: 'marketing',
    quantityOfProducts: quantityOfProducts ?? FALLBACK_VALUE,
    remarketingProductID: remarketingProductID ?? FALLBACK_VALUE,
    categoryName: category?.shortTitle || FALLBACK_VALUE,
    categoryID: category?._id || FALLBACK_VALUE,
    categoryCode: category?.code || FALLBACK_VALUE,
    categoryPath: category?.url || FALLBACK_VALUE,
    pageType: pageType.replace('/', ''),
  });
};

export interface RecommendationRequestPayload {
  service: string;
  prudsysTestGroup: string;
}

export const recommendationRequest = (gtm: GtmInteractor) => (payload: RecommendationRequestPayload) => {
  const { service, prudsysTestGroup } = payload;
  return gtm.push({
    eventAction: service,
    eventLabel: 'NA',
    prudsysTestGroup,
    event: 'prudsys',
    eventCategory: 'recommendation_request',
  });
};

type SessionStartEventPayload = {
  locale: string;
  environment: string;
  clientID: string;
  originalLocation: string;
  abTestTitle: string;
  abTestVariant: string;
  distributionFactor: number;
};

export const sessionStart =
  (gtm: GtmInteractor) =>
  ({
    clientID,
    environment,
    locale,
    originalLocation,
    abTestTitle,
    distributionFactor,
    abTestVariant,
  }: SessionStartEventPayload) => {
    return gtm.pushOnce({
      event: 'sessionStart',
      clientID,
      environment,
      pageCountry: locale,
      abTestTitle,
      abTestVariant,
      abTestFactor: distributionFactor,
      originalLocation,
    });
  };

export const newsletterRegistered = (gtm: GtmInteractor) => () => {
  return gtm.push({
    event: 'event',
    eventCategory: 'newsletter',
    eventAction: 'newsletter_registration',
    eventLabel: 'success',
  });
};
